import React, { useState } from 'react';
import { useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';

const StripePaymentForm = ({ amount, onSuccess, onError, courseId, studentInfo }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [isProcessing, setIsProcessing] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [retryCount, setRetryCount] = useState(0);
    const MAX_RETRIES = 3;

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!stripe || !elements) {
            return;
        }

        if (isProcessing) {
            console.log('⚠️ Payment already processing, ignoring duplicate submission');
            return;
        }

        setIsProcessing(true);
        setErrorMessage('');

        try {
            console.log('💳 Confirming payment...');
            const { error, paymentIntent } = await stripe.confirmPayment({
                elements,
                confirmParams: {
                    return_url: `${window.location.origin}/payment-success`,
                },
                redirect: 'if_required',
            });

            if (error) {
                console.error('❌ Payment error:', error);

                // Handle specific error types
                if (error.type === 'card_error' || error.type === 'validation_error') {
                    setErrorMessage(error.message);
                } else if (error.message && error.message.includes('402')) {
                    setErrorMessage('Payment processing is currently unavailable. Please try a different payment method or contact support.');
                } else {
                    setErrorMessage(error.message || 'Payment failed. Please try again.');
                }

                setIsProcessing(false);
                setRetryCount(prev => prev + 1);

                if (onError) onError(error);

                // Stop processing after max retries
                if (retryCount >= MAX_RETRIES) {
                    setErrorMessage(`Payment failed after ${MAX_RETRIES} attempts. Please contact support or try a different payment method.`);
                }
            } else if (paymentIntent && paymentIntent.status === 'succeeded') {
                // Payment successful
                console.log('✅ Payment succeeded:', paymentIntent.id);
                setRetryCount(0);

                // Verify payment and trigger receipt generation
                try {
                    const verifyResponse = await fetch(
                        `${process.env.REACT_APP_API_URL}/stripe/verify-payment/${paymentIntent.id}`,
                        { method: 'GET' }
                    );
                    const verifyData = await verifyResponse.json();
                    console.log('📧 Payment verified, receipt should be generated:', verifyData);
                } catch (verifyError) {
                    console.warn('⚠️ Failed to verify payment for receipt:', verifyError);
                }

                if (onSuccess) {
                    onSuccess({
                        paymentId: paymentIntent.id,
                        amount: paymentIntent.amount / 100,
                        status: paymentIntent.status
                    });
                }
            } else if (paymentIntent) {
                // Payment in other state (requires_action, processing, etc.)
                console.log('⚠️ Payment status:', paymentIntent.status);
                setErrorMessage(`Payment status: ${paymentIntent.status}. Please wait or contact support.`);
                setIsProcessing(false);
            }
        } catch (err) {
            console.error('❌ Exception during payment:', err);
            setErrorMessage('An unexpected error occurred. Please try again.');
            setIsProcessing(false);
            if (onError) onError(err);
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            <PaymentElement />

            {errorMessage && (
                <div className="alert alert-danger mt-3" role="alert">
                    <i className="fas fa-exclamation-circle me-2"></i>
                    {errorMessage}
                </div>
            )}

            <button
                type="submit"
                disabled={!stripe || isProcessing}
                className="btn btn-lg w-100 text-white fw-bold mt-4"
                style={{ backgroundColor: '#6B8E23', border: 'none' }}
            >
                {isProcessing ? (
                    <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Processing Payment...
                    </>
                ) : (
                    <>
                        <i className="fas fa-lock me-2"></i>
                        Pay ${amount}
                    </>
                )}
            </button>

            <div className="text-center mt-3">
                <small className="text-muted">
                    <i className="fas fa-shield-alt me-1"></i>
                    Secure payment processing
                </small>
            </div>
        </form>
    );
};

export default StripePaymentForm;
